import React from 'react';
import { Box, Container, Typography, Link, Grid, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import FacebookIcon from '@mui/icons-material/Facebook';
import YouTubeIcon from '@mui/icons-material/YouTube';
import SchoolIcon from '@mui/icons-material/School';
import LaunchIcon from '@mui/icons-material/Launch';

const StyledFooter = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  padding: theme.spacing(4, 0, 2),
  width: '100%',
}));

const FooterSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
}));

const FooterHeading = styled(Typography)(({ theme }) => ({
  fontSize: '1.1rem',
  fontWeight: 600,
  marginBottom: theme.spacing(2),
}));

const ContactItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(1),
  '& .MuiSvgIcon-root': {
    marginRight: theme.spacing(1),
    fontSize: '1.2rem',
  },
}));

const UniversitySection = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  border: '1px solid rgba(255, 255, 255, 0.2)',
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    border: '1px solid rgba(255, 255, 255, 0.4)',
    transform: 'translateY(-2px)',
  },
}));

const UniversityLink = styled(Link)(({ theme }) => ({
  color: 'white',
  textDecoration: 'none',
  display: 'block',
  fontSize: '1rem',
  fontWeight: 500,
  transition: 'all 0.3s ease',
  '&:hover': {
    textDecoration: 'none',
    color: 'rgba(255, 255, 255, 0.9)',
  },
}));

const QuickInfo = styled(Typography)(({ theme, islanguageenglish }) => ({
  fontSize: islanguageenglish === 'true' ? '1rem' : '0.85rem',
  lineHeight: 1.8,
  textAlign: 'justify',
}));

const SocialButton = styled(IconButton)(({ theme }) => ({
  color: 'white',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  '&:hover': {
    backgroundColor: 'white',
    color: theme.palette.primary.main,
  },
}));

const LocationLink = styled(Link)(({ theme }) => ({
  color: 'white',
  textDecoration: 'none',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(1, 0),
  '&:hover': {
    textDecoration: 'underline',
  },
}));

const CopyrightSection = styled(Box)(({ theme }) => ({
  borderTop: '1px solid rgba(255, 255, 255, 0.1)',
  marginTop: theme.spacing(2),
  paddingTop: theme.spacing(2),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: theme.spacing(1),
    textAlign: 'center',
  },
}));

const Footer = ({ language = 'mm' }) => {
  const currentYear = new Date().getFullYear();

  const content = {
    contact: {
      en: 'Contact Us',
      mm: 'ဆက်သွယ်ရန်',
    },
    quickInfo: {
      title: {
        en: 'About Monastery',
        mm: 'ကျောင်းတိုက်အကြောင်း',
      },
      text: {
        en: 'With reverence to the proclamation made by the ancient venerable monks, which states, "In the future, the Sasana (Buddhist Order) will be preserved only by virtuous and modest monks," this monastery is striving to uphold and advance the Sasana, which is deteriorating in various aspects. Recognizing that merely teaching and learning literature without adhering to the Vinaya (monastic discipline) and the Suttas (discourses) is no longer sufficient, the monastery has adopted the policy of "Reforming conduct and teaching knowledge to elevate the Sasana" and is moving forward with this aim.',
        mm: '"အနာဂတေ လဇ္ဇီ ရက္ခိဿတိ၊ လဇ္ဇီ ရက္ခိဿတိ၊ လဇ္ဇီ ရက္ခိဿတိ (အနာဂတ်သာသနာတော်ကို သီလဝန္တလဇ္ဇီ ရဟန်းတော်တို့ကသာ စောင့်ရှောက်ကြလိမ့်မည်)"ဟူသော ရှေးရဟန္တာ မထေရ်မြတ်တို့ ကြွေးကြော်တော်မူချက်ကို ဦးထိပ်တင် ခံယူကြ၍ အဖက်ဖက်က ယိုယွင်းလာနေသည့် သာသနာတော်မြတ်ကို ဝိနည်းပဋိပတ် သုတ္တန်ပဋိပတ်မပါဘဲ စာပေသင်ကြားပို့ချနေရုံမျှဖြင့် မလုံလောက်တော့သောကြောင့် "အကျင့်လည်းပြင် စာလည်းသင် မြှင့်တင်သာသနာ"ဟူသော မူဝါဒကို ချမှတ်လျက် ဦးတည်လျှောက်လှမ်းနေသော တောရစာသင်တိုက် တစ်ခု ဖြစ်ပါသည်။',
      },
    },
    university: {
      title: {
        en: 'Our University',
        mm: 'တက္ကသိုလ်',
      },
      name: {
        en: 'Mahavihara Dhamma Vinaya University',
        mm: 'မဟာဝိဟာရ ဓမ္မဝိနယ တက္ကသိုလ်',
      },
    },
    social: {
      en: 'Follow Us',
      mm: 'လူမှုကွန်ယက်များ',
    },
    location: {
      en: 'Location',
      mm: 'တည်နေရာ',
    },
  };

  return (
    <StyledFooter>
      <Container maxWidth="xl">
        <Grid container spacing={4}>
          {/* Contact Information */}
          <Grid item xs={12} sm={6} md={3}>
            <FooterSection>
              <FooterHeading>{content.contact[language]}</FooterHeading>
              <ContactItem>
                <EmailIcon />
                <Link 
                  href="mailto:admin@mahavihara-myanmar.org"
                  color="inherit"
                  sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                  admin@mahavihara-myanmar.org
                </Link>
              </ContactItem>
              <ContactItem>
                <PhoneIcon />
                <Link 
                  href="tel:+959792573820"
                  color="inherit"
                  sx={{ textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                  09792573820
                </Link>
              </ContactItem>

              <UniversitySection>
                <FooterHeading sx={{ mb: 1 }}>
                  {content.university.title[language]}
                </FooterHeading>
                <UniversityLink
                  href="http://www.mahavihara-international.org"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SchoolIcon />
                    {content.university.name[language]}
                  </Box>
                  <Box 
                    sx={{ 
                      fontSize: '0.85rem', 
                      mt: 1,
                      ml: 3,
                      color: 'rgba(255, 255, 255, 0.8)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      '&:hover': {
                        color: 'white'
                      }
                    }}
                  >
                    Visit Website <LaunchIcon sx={{ fontSize: '0.9rem' }} />
                  </Box>
                </UniversityLink>
              </UniversitySection>
            </FooterSection>
          </Grid>

          {/* Quick Info */}
          <Grid item xs={12} md={6}>
            <FooterSection>
              <FooterHeading>{content.quickInfo.title[language]}</FooterHeading>
              <QuickInfo 
                variant="body2" 
                islanguageenglish={language === 'en' ? 'true' : 'false'}
              >
                {content.quickInfo.text[language]}
              </QuickInfo>
            </FooterSection>
          </Grid>

          {/* Social Media and Location */}
          <Grid item xs={12} sm={6} md={3}>
            <FooterSection>
              <FooterHeading>{content.social[language]}</FooterHeading>
              <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
                <SocialButton 
                  href="https://facebook.com" 
                  target="_blank"
                  aria-label="Facebook"
                >
                  <FacebookIcon />
                </SocialButton>
                <SocialButton 
                  href="https://youtube.com" 
                  target="_blank"
                  aria-label="YouTube"
                >
                  <YouTubeIcon />
                </SocialButton>
              </Box>

              <FooterHeading>{content.location[language]}</FooterHeading>
              <LocationLink 
                href="https://maps.app.goo.gl/yDVFBVopNJfsrjfS9"
                target="_blank"
                rel="noopener noreferrer"
              >
                <LocationOnIcon />
                {language === 'en' ? 
                  'View on Google Maps' : 
                  'မြေပုံတွင်ကြည့်ရှုရန်'
                }
              </LocationLink>
            </FooterSection>
          </Grid>
        </Grid>

        {/* Copyright Section */}
        <CopyrightSection>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            © {currentYear} Mahavihara Forest Monastery. All rights reserved.
          </Typography>
          <Link 
            href="https://studivert.com" 
            target="_blank" 
            rel="noopener noreferrer"
            sx={{ 
              color: 'white', 
              textDecoration: 'none',
              opacity: 0.9,
              '&:hover': { 
                opacity: 1,
                textDecoration: 'none',
              },
            }}
          >
            Developed by StudiVert
          </Link>
        </CopyrightSection>
      </Container>
    </StyledFooter>
  );
};

export default Footer; 